import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import {
  retailSales,
  type FiscalStatus,
  type RetailSaleListItem,
} from '@/api/retailSales';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import HintBadge from '@/components/HintBadge';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { useAttachmentFlags } from '@/shared/hooks/useAttachmentFlags';
import { pickDefaultOrg } from '@/store/defaults';

const PERMISSION = 'retail.sale.create';

const FISCAL_LABEL: Record<string, string> = {
  not_fiscalized: '— Не фіскалізовано',
  pending: '⏳ В очікуванні',
  fiscalized: '✓ Фіскалізовано',
  failed: '⚠ Помилка',
  demo: 'D Демо',
};

const PAYMENT_LABEL: Record<string, string> = {
  cash: 'Готівка',
  card: 'Картка',
  mixed: 'Змішана',
  transfer: 'Переказ',
};

export default function RetailSalesPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const [orgId, setOrgId] = useState<string>('');
  const [fiscalFilter, setFiscalFilter] = useState<string>('');
  const [showBackdatedOnly, setShowBackdatedOnly] = useState(false);
  useEffect(() => {
    const first = pickDefaultOrg(orgList.data?.items);
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['retail-sales', orgId, fiscalFilter],
    queryFn: () =>
      retailSales.list({
        organization_id: orgId || undefined,
        fiscal_status: fiscalFilter ? (fiscalFilter as FiscalStatus) : undefined,
        limit: 500,
      }),
    enabled: !!orgId,
  });

  // is_backdated — UI-only filter (backend не має query-param).
  const rowData = useMemo(() => {
    const items = list.data?.items ?? [];
    return showBackdatedOnly ? items.filter((s) => s.is_backdated) : items;
  }, [list.data, showBackdatedOnly]);

  const cols = useMemo<ColumnDef<RetailSaleListItem>[]>(
    () => [
      {
        key: 'document_number',
        header: 'Чек №',
        width: 150,
        sortValue: (r) => r.document_number ?? '',
        cell: (r) => r.document_number,
      },
      {
        key: 'document_date',
        header: 'Дата/час',
        width: 180,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => (r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : ''),
      },
      {
        key: 'pos_workstation_id',
        header: 'POS',
        width: 150,
        sortValue: (r) => r.pos_workstation_id ?? '',
        cell: (r) => r.pos_workstation_id,
      },
      {
        key: 'cashier_user_id',
        header: 'Касир',
        width: 150,
        sortValue: (r) => r.cashier_user_id ?? '',
        cell: (r) => r.cashier_user_id,
      },
      {
        key: 'total_amount',
        header: 'Сума',
        width: 130,
        align: 'right',
        sortValue: (r) => Number(r.total_amount),
        cell: (r) =>
          r.total_amount
            ? Number(r.total_amount).toLocaleString('uk-UA', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : '',
      },
      {
        key: 'payment_method',
        header: 'Оплата',
        width: 120,
        sortValue: (r) => PAYMENT_LABEL[r.payment_method] ?? r.payment_method,
        cell: (r) => PAYMENT_LABEL[r.payment_method] ?? r.payment_method,
      },
      {
        key: 'fiscal_status',
        header: 'ПРРО',
        width: 170,
        sortValue: (r) => FISCAL_LABEL[r.fiscal_status] ?? r.fiscal_status,
        cell: (r) => FISCAL_LABEL[r.fiscal_status] ?? r.fiscal_status,
      },
      {
        key: 'fiscal_number',
        header: 'Фіск. №',
        width: 140,
        sortValue: (r) => r.fiscal_number ?? '',
        cell: (r) => r.fiscal_number,
      },
      {
        key: 'is_backdated',
        header: 'Заднім числом',
        width: 150,
        sortValue: (r) => (r.is_backdated ? 1 : 0),
        cell: (r) => (r.is_backdated ? '⚠ Так' : '—'),
      },
      {
        key: 'posted',
        header: 'Проведено',
        width: 130,
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ Проведено' : 'Чернетка'),
      },
    ],
    [],
  );

  const canCreate = !!orgId && allowedOrgIds.has(orgId);
  const tooltipNew = !orgId
    ? 'Спершу оберіть юрособу'
    : !canCreate
      ? `Немає прав створювати чеки (${PERMISSION}). У вас є право у ${perms.availableOrgs.length} з ${perms.totalAccessibleOrgs} юросіб`
      : undefined;

  // 2026-08-23: колонки «Вкладення»/«Підпис» у списку документів.
  const _rowsForCols = rowData;
  const att = useAttachmentFlags<RetailSaleListItem>('doc.retail_sales', _rowsForCols);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
          Чеки роздрібного продажу
          <HintBadge
            title="Що це?"
            text={
              'Журнал усіх роздрібних чеків (касових продажів через POS).\n\n' +
              'Звичайний касир пробиває чеки через /pos інтерфейс — вони з\'являються тут.\n\n' +
              'Через цей бух-UI можна:\n' +
              '• Передивлятись історію чеків (фільтри: ПРРО-статус, заднім числом).\n' +
              '• Створити «коригуючий» чек заднім числом (галочка is_backdated) — ' +
              'для виправлення помилок ДО фіскалізації або документування пропущених продажів.\n' +
              '• Фіскалізувати «pending» чеки якщо ПРРО був недоступний.\n' +
              '• Анулювати помилкові чеки (тільки до фіскалізації).\n\n' +
              'УВАГА: заднім числом створені чеки НЕ фіскалізуються через ПРРО ' +
              '(тільки звичайні проводки 702/93/301).'
            }
          />
        </h2>
        <select
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          style={{ width: 140, flex: '0 0 auto' }}
        >
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name} — {o.code}
            </option>
          ))}
        </select>
        <select
          value={fiscalFilter}
          onChange={(e) => setFiscalFilter(e.target.value)}
          style={{ width: 140, flex: '0 0 auto' }}
        >
          <option value="">Всі статуси ПРРО</option>
          <option value="not_fiscalized">Не фіскалізовано</option>
          <option value="pending">В очікуванні</option>
          <option value="fiscalized">Фіскалізовано</option>
          <option value="failed">Помилка</option>
          <option value="demo">Демо</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, flex: '0 0 auto' }}>
          <input
            type="checkbox"
            checked={showBackdatedOnly}
            onChange={(e) => setShowBackdatedOnly(e.target.checked)}
          />
          Лише заднім числом
        </label>
        <button
          className="primary"
          style={{ marginLeft: 'auto' }}
          disabled={!canCreate}
          title={tooltipNew}
          onClick={() => navigate('/retail/retail-sales/new')}
        >
          + Новий чек
        </button>
      </div>
      <DataTable<RetailSaleListItem>
        rows={rowData}
        columns={[...cols, ...att.column]}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає чеків."
        onRowDoubleClick={(r) => navigate(`/retail/retail-sales/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
